import React from "react";
import { useLang } from "@/i18n/LanguageContext";
import { RevealText, FadeUp, Counter } from "@/components/site/Reveal";
import { IMAGES } from "@/data/images";

const About = () => {
  const { t, lang } = useLang();
  return (
    <div data-testid="about-page" className="pt-28 md:pt-36">
      {/* Hero */}
      <section className="mx-auto max-w-[1440px] px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-8">
            <div className="text-[11px] uppercase tracking-[0.32em] text-white/50 mb-6">{t.about.eyebrow}</div>
            <RevealText lines={[t.about.title]} as="h1" className={`display text-white ${lang === "ur" ? "text-lg sm:text-xl md:text-2xl lg:text-3xl" : lang === "en" ? "text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.05]" : "text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.92]"}`} />
            <FadeUp delay={0.2}>
              <p className={`mt-10 text-white/70 max-w-3xl leading-relaxed ${lang === "ur" ? "text-base" : "text-lg md:text-xl"}`}>{t.about.body}</p>
            </FadeUp>
          </div>
          <FadeUp delay={0.15} className="md:col-span-4">
            <div className="border-t border-white/15 pt-6">
              <div className="display text-white text-6xl md:text-7xl leading-none">{t.about.yearsN}</div>
              <div className="text-[11px] uppercase tracking-[0.3em] text-white/50 mt-3">{t.about.years}</div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Image spot */}
      <section className="mx-auto max-w-[1440px] px-6 md:px-10 mt-20 md:mt-32">
        <FadeUp>
          <div className="spot-image rounded-md overflow-hidden">
            <img src={IMAGES.saudi_office} alt="Saudi office" className="w-full h-[52vh] md:h-[68vh] object-cover grayscale-[20%] contrast-125" />
          </div>
        </FadeUp>
      </section>

      {/* Vision + Mission */}
      <section className="mx-auto max-w-[1440px] px-6 md:px-10 py-24 md:py-40">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
          {[t.about.vision, t.about.mission].map((v, i) => (
            <FadeUp key={i} delay={i * 0.1}>
              <div className="sw-card rounded-md p-10">
                <div className="chapter-num text-4xl mb-6">0{i + 1}</div>
                <div className="display text-3xl md:text-4xl text-white mb-4">{v.t}</div>
                <div className="text-white/65 leading-relaxed">{v.d}</div>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* Strengths */}
      <section className="border-t border-white/10">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-24 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 md:gap-10">
            {t.about.strengths.map((s, i) => (
              <FadeUp key={i} delay={i * 0.08}>
                <div className="border-t border-white/15 pt-6">
                  <div className="chapter-num text-3xl mb-4">0{i + 1}</div>
                  <div className="display text-2xl text-white mb-3">{s.t}</div>
                  <div className="text-white/60 text-sm leading-relaxed">{s.d}</div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Audience */}
      <section className="mx-auto max-w-[1440px] px-6 md:px-10 py-24 md:py-40">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <FadeUp className="md:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.32em] text-white/50 mb-6">04 —</div>
            <RevealText
              lines={[t.about.audiences.t]}
              as="h2"
              className={`display text-white leading-[1.05] ${lang === "ur" ? "text-3xl md:text-5xl" : "text-4xl md:text-6xl lg:text-7xl"}`}
            />
          </FadeUp>
          <div className="md:col-span-7 space-y-6">
            {t.about.audiences.items.map((a, i) => (
              <FadeUp key={i} delay={i * 0.1}>
                <div className="flex items-baseline gap-6 border-b border-white/10 pb-6">
                  <span className="chapter-num text-sm w-8">0{i + 1}</span>
                  <span className={`text-white ${lang === "ur" ? "text-2xl" : "display text-3xl md:text-5xl"}`}>{a}</span>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
